import React, { ContextType, ReactNode, useMemo, useState } from "react";

import { DateTime } from "luxon";
import * as R from "ramda";

import MenuGroup from "api/types/MenuGroup";
import Schedule from "api/types/Schedule";
import { AvailableOrderTypes } from "api/types/enums";
import excludeFromIntervals from "helpers/excludeFromIntervals";
import shiftDeliveryOrPickupIntervals from "helpers/shiftDeliveryOrPickupIntervals";
import unwrapSchedule from "helpers/unwrapSchedule";
import useBusiness from "hooks/useBusiness";
import StateOf from "utility/StateOf";
import profile from "utility/profile";
import toArray from "utility/toArray";

import CartContext from "./CartContext";

const DATE_TIME_MIN = DateTime.fromISO("0001-01-01");
const DATE_TIME_MAX = DateTime.fromISO("9999-01-01");
const TIME_SLOT_ROUND = 1000 * 60 * 15; // 15 mins

type Context = ContextType<typeof CartContext>;
type State = StateOf<Context>;

const initialState: State = {
	isConfirmationVisible: false,
	cartItemsCount: 0,
	cartItems: [],
	cartItemByLocalId: {},

	cartTotalCents: 0,
	taxCents: null,
	tipCents: null,
	tipPercentage: 15,
	discount: 0,
	percentOff: 0,
	promoCode: null,
	grandTotalCents: 0,
	orderType: null,
	deliveryOrPickupAt: undefined,
	preparationTimeMinutes: 25,

	fullName: undefined,
	phoneNumber: undefined,
	email: undefined,
	deliveryAddress: null,

	paymentInfo: null
};

const initializer = (): State => {
	try {
		const json = localStorage.getItem("cart");

		if (json) {
			const state = JSON.parse(json);
			state.deliveryOrPickupAt = state.deliveryOrPickupAt ? DateTime.fromISO(state.deliveryOrPickupAt) : null;
			state.cartItemByLocalId = R.fromPairs(state.cartItems.map(x => [x.localId, x]));
			return state;
		}
	} catch (exc) {
		console.warn("failed to restore cart state from local storage:\r\n", exc);
	}

	return initialState;
};

interface Props {
	children: ReactNode;
}

const CartContextProvider = ({ children }: Props) => {
	const [state, setState] = useState(initializer());

	const { business } = useBusiness();

	const orderType = state.orderType || business.orderTypes[0];

	const effectivePreparationTimeMinutes = useMemo(() => {
		return R.apply(Math.max, [
			orderType.defaultPreparationTimeMinutes,
			...state.cartItems
				.flatMap(item => {
					if (item.selectedOptions?.length) {
						const allOptions = item.menuItem.menuItemOptionGroups.flatMap(g =>
							g.menuItemOptions.map(o => ({
								menuItemOptionGroupId: g.id,
								menuItemOptionId: o.id,
								menuItemOption: o
							}))
						);

						const selectedOptions = allOptions.filter(x =>
							item.selectedOptions.find(
								y => y.menuItemOptionGroupId === x.menuItemOptionGroupId && y.menuItemOptionId === x.menuItemOptionId
							)
						);

						const optionsPreparationTimeMinutes = selectedOptions
							.map(x => x.menuItemOption.preparationTimeMinutes)
							.filter(Boolean);

						if (optionsPreparationTimeMinutes.length) {
							return optionsPreparationTimeMinutes;
						}
					}
					return [item.menuItem.preparationTimeMinutes];
				})
				.filter(Boolean)
		]);
	}, [orderType?.defaultPreparationTimeMinutes, state.cartItems]);

	const getUnwrappedAvailability = useMemo(() => {
		let ran = false;
		let cachedDays: number;
		let cachedDate: string;

		let cachedResult: {
			date: string;
			intervals: {
				startsAt: DateTime;
				endsAt: DateTime;
			}[];
		}[];

		const recalc = profile("CartContextProvider.getUnwrappedAvailability()")((days: number) => {
			if (!orderType?.effectiveAvailability) {
				return [];
			}

			const generator = unwrapSchedule({
				schedule: orderType.effectiveAvailability,
				tz: business.timeZone,
				start: "today"
			});

			return toArray(generator, { count: days });
		});

		return () => {
			const days = business.orderAheadMaxDays || 5;
			const now = DateTime.utc().setZone(business.timeZone);
			const date = now.toISODate();

			if (!ran || days !== cachedDays || date !== cachedDate) {
				cachedResult = recalc(days);
				cachedDate = date;
				cachedDays = days;
				ran = true;
			}

			return cachedResult;
		};
	}, [business.orderAheadMaxDays, business.timeZone, orderType?.effectiveAvailability]);

	const getDeliveryOrPickupIntervals = useMemo(() => {
		let ran = false;
		let cachedSlot: number;

		let cachedAvailability: {
			date: string;
			intervals: {
				startsAt: DateTime;
				endsAt: DateTime;
			}[];
		}[];

		let cachedResult: {
			date: string;
			intervals: {
				startsAt: DateTime;
				endsAt: DateTime;
			}[];
		}[];

		const recalc = profile("CartContextProvider.getDeliveryOrPickupIntervals()")(
			(
				unwrappedAvailability: {
					date: string;
					intervals: {
						startsAt: DateTime;
						endsAt: DateTime;
					}[];
				}[]
			) => {
				return unwrappedAvailability.map(x => ({
					date: x.date,
					intervals: shiftDeliveryOrPickupIntervals(
						// NOTE: exclude past from intervals
						excludeFromIntervals(
							x.intervals.map(x => [x.startsAt, x.endsAt]),
							[[undefined, DateTime.utc().setZone(business.timeZone)]]
						).map(x => ({ startsAt: x[0] || DATE_TIME_MIN, endsAt: x[1] || DATE_TIME_MAX })),
						effectivePreparationTimeMinutes +
							((orderType?.orderType === AvailableOrderTypes.delivery && orderType?.deliveryTimeMaxMinutes) || 0)
					)
				}));
			}
		);

		return () => {
			const slot = Math.floor(+new Date() / TIME_SLOT_ROUND) * TIME_SLOT_ROUND;
			const availability = getUnwrappedAvailability();

			if (!ran || slot !== cachedSlot || availability !== cachedAvailability) {
				cachedResult = recalc(availability);
				cachedAvailability = availability;
				cachedSlot = slot;
				ran = true;
			}

			return cachedResult;
		};
	}, [
		business.timeZone,
		orderType?.orderType,
		orderType?.deliveryTimeMaxMinutes,
		effectivePreparationTimeMinutes,
		getUnwrappedAvailability
	]);

	/**
	 * Selected deliveryOrPickupAt or undefined if not selected or invalid / less than now
	 */
	const getEffectiveDeliveryOrPickupAt = useMemo(() => {
		let ran = false;

		let cachedIntervals: {
			date: string;
			intervals: {
				startsAt: DateTime;
				endsAt: DateTime;
			}[];
		}[];

		let cachedResult: undefined | DateTime;

		const recalc = profile("CartContextProvider.getEffectiveDeliveryOrPickupAt()")(
			(
				intervals: {
					date: string;
					intervals: {
						startsAt: DateTime;
						endsAt: DateTime;
					}[];
				}[]
			) => {
				const min = DateTime.utc().setZone(business.timeZone).plus({ minutes: effectivePreparationTimeMinutes });

				const candidate =
					state.deliveryOrPickupAt && +state.deliveryOrPickupAt >= +min
						? state.deliveryOrPickupAt.setZone(business.timeZone)
						: undefined;

				if (candidate) {
					const day = intervals.find(x => x.date === candidate.toISODate());

					if (day && day.intervals.find(x => +x.startsAt <= +candidate && +x.endsAt >= +candidate)) {
						return candidate;
					}
				}

				return undefined;
			}
		);

		return () => {
			const intervals = getDeliveryOrPickupIntervals();

			if (!ran || intervals !== cachedIntervals) {
				cachedResult = recalc(intervals);
				cachedIntervals = intervals;
				ran = true;
			}

			return cachedResult;
		};
	}, [business.timeZone, effectivePreparationTimeMinutes, getDeliveryOrPickupIntervals, state.deliveryOrPickupAt]);

	const getDisplayDeliveryOrPickupAt = useMemo(() => {
		let ran = false;
		let cachedEffective: undefined | DateTime;
		let cachedResult: undefined | DateTime;

		const recalc = profile("CartContextProvider.getDisplayDeliveryOrPickupAt()")((effective: undefined | DateTime) => {
			if (effective) {
				return effective;
			}

			const intervals = getDeliveryOrPickupIntervals();
			const min = DateTime.utc()
				.setZone(business.timeZone)
				.plus({
					minutes:
						effectivePreparationTimeMinutes +
						((orderType?.orderType === AvailableOrderTypes.delivery && orderType?.deliveryTimeMaxMinutes) || 0)
				});

			for (const each of intervals) {
				for (const interval of each.intervals) {
					if (+interval.endsAt > +min) {
						return +interval.startsAt > +min ? interval.startsAt : min;
					}
				}
			}

			return undefined;
		});

		return () => {
			const effective = getEffectiveDeliveryOrPickupAt();

			if (!ran || cachedEffective !== effective) {
				cachedResult = recalc(effective);
				cachedEffective = effective;
				ran = true;
			}

			return cachedResult;
		};
	}, [
		business.timeZone,
		effectivePreparationTimeMinutes,
		getDeliveryOrPickupIntervals,
		getEffectiveDeliveryOrPickupAt
	]);

	const areAllItemsAvailable = useMemo(() => {
		let ran = false;
		let cachedDeliveryOrPickupAt: undefined | DateTime;
		let cachedMenuGroups: Pick<MenuGroup, "id" | "availability">[];
		let cachedResult;

		const recalc = (deliveryOrPickupAt: undefined | DateTime, menuGroups: Pick<MenuGroup, "availability">[]) => {
			if (!deliveryOrPickupAt || menuGroups.length === 0) {
				return true;
			}

			const date = deliveryOrPickupAt.setZone(business.timeZone).startOf("day").toISODate();
			const schedules = menuGroups.map(x => x.availability!).filter(Boolean);

			for (const schedule of schedules) {
				const generator = unwrapSchedule({
					tz: business.timeZone,
					start: date,
					schedule
				});

				const items = toArray(generator, { count: 1 });

				const [intervals] = items.map(x =>
					shiftDeliveryOrPickupIntervals(
						// NOTE: exclude past from intervals
						excludeFromIntervals(
							x.intervals.map(x => [x.startsAt, x.endsAt]),
							[[undefined, DateTime.utc().setZone(business.timeZone)]]
						).map(x => ({ startsAt: x[0] || DATE_TIME_MIN, endsAt: x[1] || DATE_TIME_MAX })),
						effectivePreparationTimeMinutes
					)
				);

				if (!intervals) {
					return false;
				}

				const interval = intervals.find(
					interval =>
						(!interval.startsAt || +interval.startsAt <= +deliveryOrPickupAt) &&
						(!interval.endsAt || +interval.endsAt >= +deliveryOrPickupAt)
				);

				if (!interval) {
					return false;
				}
			}

			return true;
		};

		return () => {
			const deliveryOrPickupAt = getDisplayDeliveryOrPickupAt();

			const menuGroups = state.cartItems
				.map(x => x.menuItem?.menuGroup)
				.reduce((res, menuGroup) => {
					if (menuGroup && !res.find(x => x.id === menuGroup.id)) {
						res.push(menuGroup);
					}

					return res;
				}, [] as Pick<MenuGroup, "id" | "availability">[]);

			if (
				!ran ||
				deliveryOrPickupAt !== cachedDeliveryOrPickupAt ||
				menuGroups.length !== cachedMenuGroups.length ||
				menuGroups.find(x => !cachedMenuGroups.find(y => x.id === y.id)) ||
				cachedMenuGroups.find(x => !menuGroups.find(y => x.id === y.id))
			) {
				cachedResult = recalc(deliveryOrPickupAt, menuGroups);
				cachedMenuGroups = menuGroups;
				cachedDeliveryOrPickupAt = deliveryOrPickupAt;
				ran = true;
			}

			return cachedResult;
		};
	}, [business.timeZone, effectivePreparationTimeMinutes, getDisplayDeliveryOrPickupAt, state.cartItems]);

	const isItemAvailable = useMemo(() => {
		let ran = false;
		let cachedDeliveryOrPickupAt: undefined | DateTime;
		let cachedSchedule: undefined | Schedule;
		let cachedResult;

		const recalc = (deliveryOrPickupAt: undefined | DateTime, schedule: undefined | Schedule) => {
			if (!deliveryOrPickupAt || !schedule) {
				return true;
			}

			const date = deliveryOrPickupAt.setZone(business.timeZone).startOf("day").toISODate();
			const generator = unwrapSchedule({
				tz: business.timeZone,
				start: date,
				schedule
			});

			const [intervals] = toArray(generator, { count: 1 }).map(x =>
				shiftDeliveryOrPickupIntervals(
					// NOTE: exclude past from intervals
					excludeFromIntervals(
						x.intervals.map(x => [x.startsAt, x.endsAt]),
						[[undefined, DateTime.utc().setZone(business.timeZone)]]
					).map(x => ({ startsAt: x[0] || DATE_TIME_MIN, endsAt: x[1] || DATE_TIME_MAX })),
					effectivePreparationTimeMinutes
				)
			);

			console.log("intervals:", intervals);

			if (!intervals) {
				return false;
			}

			const interval = intervals.find(
				interval =>
					(!interval.startsAt || +interval.startsAt <= +deliveryOrPickupAt) &&
					(!interval.endsAt || +interval.endsAt >= +deliveryOrPickupAt)
			);

			return !!interval;
		};

		return (menuItem?: undefined | null | { menuGroup?: undefined | null | Pick<MenuGroup, "availability"> }) => {
			const deliveryOrPickupAt = getDisplayDeliveryOrPickupAt();
			const schedule = menuItem?.menuGroup?.availability || undefined;

			if (!ran || deliveryOrPickupAt !== cachedDeliveryOrPickupAt || schedule !== cachedSchedule) {
				cachedResult = recalc(deliveryOrPickupAt, schedule);
				cachedSchedule = schedule;
				cachedDeliveryOrPickupAt = deliveryOrPickupAt;
				ran = true;
			}

			return cachedResult;
		};
	}, [business.timeZone, effectivePreparationTimeMinutes, getDisplayDeliveryOrPickupAt]);

	const context = useMemo<Context>(
		() => ({
			...state,
			setState,
			getDeliveryOrPickupIntervals,
			getEffectiveDeliveryOrPickupAt,
			getDisplayDeliveryOrPickupAt,
			areAllItemsAvailable,
			isItemAvailable
		}),
		[
			state,
			setState,
			getDeliveryOrPickupIntervals,
			getDisplayDeliveryOrPickupAt,
			getEffectiveDeliveryOrPickupAt,
			areAllItemsAvailable,
			isItemAvailable
		]
	);

	return <CartContext.Provider value={context}>{children}</CartContext.Provider>;
};

export default CartContextProvider;
